'use client'

import React, { createContext, useCallback, useEffect, useState } from 'react'

import canUseDOM from '@/utilities/canUseDOM'

import { defaultTheme, getImplicitPreference, themeLocalStorageKey } from './shared'
import { themeIsValid } from './types'

import type { Theme, ThemeContextType } from './types'

const initialContext: ThemeContextType = {
  setTheme: () => null,
  theme: undefined,
}

const ThemeContext = createContext(initialContext)

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  // Initialize state from the DOM attribute that InitTheme script already set
  // This prevents any flash since InitTheme runs beforeInteractive
  const [theme, setThemeState] = useState<Theme | undefined>(() => {
    if (!canUseDOM) return undefined
    const currentTheme = document.documentElement.getAttribute('data-theme') as Theme
    // If InitTheme already set a valid theme, use it
    if (themeIsValid(currentTheme)) {
      return currentTheme
    }
    return defaultTheme
  })

  const setTheme = useCallback((themeToSet: Theme | null) => {
    if (themeToSet === null) {
      window.localStorage.removeItem(themeLocalStorageKey)
      const implicitPreference = getImplicitPreference()
      document.documentElement.setAttribute('data-theme', implicitPreference || '')
      if (implicitPreference) setThemeState(implicitPreference)
    } else {
      setThemeState(themeToSet)
      window.localStorage.setItem(themeLocalStorageKey, themeToSet)
      document.documentElement.setAttribute('data-theme', themeToSet)
    }
  }, [])

  // DO NOT change the theme on mount - the inline script in layout.tsx already set it
  // Only sync React state with what's already in the DOM.
  // This MUST stay a mount-only effect: it reads and writes the data-theme DOM
  // attribute (external system) and must never run again after mount, otherwise
  // it would revert user theme changes back to the initial theme.
  /* eslint-disable react-hooks/set-state-in-effect -- one-time mount sync of React
     state with the data-theme attribute the beforeInteractive script applied */
  useEffect(() => {
    if (!canUseDOM) return

    const currentDomTheme = document.documentElement.getAttribute('data-theme') as Theme
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const initialTheme = (window as any).__initialTheme

    // If theme was changed from initial, restore it (something tried to change it)
    if (initialTheme && currentDomTheme !== initialTheme && themeIsValid(initialTheme)) {
      document.documentElement.setAttribute('data-theme', initialTheme)
      setThemeState(initialTheme)
      return
    }

    // Trust whatever is in the DOM (set by inline script) and sync state only
    if (themeIsValid(currentDomTheme)) {
      setThemeState(currentDomTheme)
    } else if (initialTheme && themeIsValid(initialTheme)) {
      // DOM theme is invalid but we have initial theme - restore it
      document.documentElement.setAttribute('data-theme', initialTheme)
      setThemeState(initialTheme)
    } else {
      // Last resort fallback
      document.documentElement.setAttribute('data-theme', defaultTheme)
      setThemeState(defaultTheme)
    }
  }, []) // Empty deps - only run once on mount, and NEVER change the theme unless restoring initial
  /* eslint-enable react-hooks/set-state-in-effect */

  return <ThemeContext value={{ setTheme, theme }}>{children}</ThemeContext>
}
